<template>
  <div class="flex-col" style="height: 100%">
    <!-- 头部按钮 -->
    <div class="flex-between pdtb-8 pdlr-12">
      <div>
        <a-button :loading="loading" @click="handleRun" type="primary" icon="play-circle" title="Ctrl + Enter">
          <span>运行</span>
        </a-button>
        <a-button class="ml-8" @click="handleClear" icon="delete" title="Ctrl + Delete">
          <span>清除</span>
        </a-button>
        <a-button class="ml-8" @click="handleFormat" icon="highlight" title="Ctrl + Shift + F">
          <span>一键美化</span>
        </a-button>
      </div>
      <div>
        <a-button :loading="resetLoading" title="切换数据库用户" @click="handleSwapSchema" icon="swap" class="mr-8">
          <span class="mgl-8">{{ currentSchema }}</span>
        </a-button>
        <a-button :loading="resetLoading" @click="handleClearEnv" title="Ctrl + Shift + Delete" icon="reload">
          <span class="mgl-8">重置环境</span>
        </a-button>
      </div>
    </div>
    <!-- 编辑器插件 -->
    <div style="flex: 1; overflow-y: auto">
      <codemirror ref="cm" v-model="code" :options="cmOptions" style="height: 100%"></codemirror>
    </div>
    <a-modal v-model="showResetModal" title="重置环境" :width="700" @cancel="modalClose">
      <template slot="footer">
        <a-button key="back" @click="modalClose"> 取消 </a-button>
        <a-button key="submit" type="primary" @click="handleResetOK"> 确认 </a-button>
      </template>
      <div>
        <a-form :label-col="{ span: 4 }" :wrapper-col="{ span: 18 }">
          <a-form-item :colon="false" label=" " :label-col="{ span: 3 }" :wrapper-col="{ span: 19 }">
            <a-alert
              description="重置环境将删除所有对象，并将数据库置为初始状态。"
              style="text-align: center"
              type="error"
            />
          </a-form-item>
          <a-form-item label="归档" help="保存当前环境中执行过的SQL语句">
            <a-radio-group v-model="archive">
              <a-radio :value="true"> 是 </a-radio>
              <a-radio :value="false"> 否 </a-radio>
            </a-radio-group>
          </a-form-item>
          <a-form-item label="归档描述" v-if="archive">
            <a-textarea
              placeholder="请输入归档描述"
              :auto-size="{ minRows: 3, maxRows: 5 }"
              v-model="archiveDescription"
              :maxLength="200"
            />
          </a-form-item>
        </a-form>
      </div>
    </a-modal>
    <a-modal v-model="showSchemaModal" title="设置数据库用户" width="35%" @cancel="modalClose">
      <template slot="footer">
        <a-button key="back" @click="modalClose"> 取消 </a-button>
        <a-button key="submit" type="primary" @click="handleSwapOK"> 确认 </a-button>
      </template>
      <a-form :label-col="{ span: 5 }" :wrapper-col="{ span: 17 }">
        <a-form-item :colon="false" label=" " :label-col="{ span: 3 }" :wrapper-col="{ span: 19 }">
          <a-alert
            description="1. 默认用户/密码：SQLAB/sqlab@yashandb
  2. 设置的执行SQL的数据库用户，仅针对当前执行环境生效。"
            type="info"
            style="white-space: pre; text-align: center"
          />
        </a-form-item>

        <a-form-item :help="schemaStatus == 'success' ? '' : '用户名不可为空'" :validate-status="schemaStatus">
          <span slot="label">
            用户名&nbsp;
            <a-tooltip title="用户名大小写敏感，创建用户时不加引号置为大写，请注意区分">
              <a-icon type="question-circle-o" />
            </a-tooltip>
          </span>
          <a-input v-model="swapSchema" placeholder="请输入数据库用户名" :allowClear="true" />
        </a-form-item>
        <a-form-item
          label="密码"
          :help="schemaPwdStatus == 'success' ? '' : '用户密码不可为空'"
          :validate-status="schemaPwdStatus"
        >
          <a-input-password v-model="schemaPwd" placeholder="请输入数据库用户密码" />
        </a-form-item>
      </a-form>
    </a-modal>
  </div>
</template>

<script>
import { codemirror } from 'vue-codemirror' // 全局引入vue-codemirror
import 'codemirror/addon/search/search'
import 'codemirror/addon/search/searchcursor'
import 'codemirror/lib/codemirror.css' // 引入css文件
import 'codemirror/theme/idea.css' // 引入主题 可以从 codemirror/theme/ 下引入多个
import 'codemirror/mode/sql/sql.js' // 引入语言模式 可以从 codemirror/mode/ 下引入多个
import 'codemirror/addon/display/autorefresh'
import { resetSession, currentSchema, swapSchema } from '@/api/sessions'
import { format } from 'sql-formatter'
import { RUNNING } from '@/store/mutation-types'
import { aesEncrypt } from '@/utils/encrypt'

export default {
  name: 'EditPart',
  components: { codemirror },
  watch: {
    code(val) {
      localStorage.setItem('sqlCode', val)
    },
    swapSchema(val) {
      this.schemaStatus = val ? 'success' : 'error'
    },
    schemaPwd(val) {
      this.schemaPwdStatus = val ? 'success' : this.showSchemaModal ? 'error' : 'success'
    },
  },
  mounted() {
    document.addEventListener('keydown', this.handleKeydownEvent)
    this.getCurrentSchema()
  },
  beforeDestroy() {
    document.removeEventListener('keydown', this.handleKeydownEvent)
  },
  computed: {
    loading() {
      return this.$store.getters[RUNNING]
    },
    resetLoading() {
      return this.$store.getters[RUNNING]
    },
    codemirror() {
      return this.$refs.cm.codemirror
    },
  },
  data() {
    return {
      showResetModal: false,
      showSchemaModal: false,
      archive: true,
      archiveDescription: '',
      code: localStorage.getItem('sqlCode') || '',
      cmOptions: {
        mode: 'text/x-sql', // 语言及语法模式
        theme: 'idea', // 主题
        line: true, // 显示函数
        lineNumbers: true,
        lineWrapping: true, // 软换行
        tabSize: 2, // tab宽度
        // 高亮行功能
        styleActiveLine: true,
        // 自动括号匹配功能
        matchBrackets: true,
        autoRefresh: true,
      },
      currentSchema: 'SQLAB',
      swapSchema: 'SQLAB',
      schemaPwd: '',
      schemaStatus: 'success',
      schemaPwdStatus: 'success',
    }
  },
  methods: {
    getCurrentSchema() {
      currentSchema().then((res) => {
        this.currentSchema = res.content.name
        this.swapSchema = this.currentSchema
      })
    },
    handleKeydownEvent(e) {
      if (e.key === 'Enter' && e.ctrlKey) {
        e.preventDefault()
        this.handleRun()
      } else if (e.ctrlKey && e.shiftKey && (e.key === 'Delete' || e.code === 'KeyD' || e.keyCode === 46)) {
        e.preventDefault()
        this.handleClearEnv()
      } else if ((e.key === 'Delete' || e.keyCode === 46) && e.ctrlKey) {
        e.preventDefault()
        this.handleClear()
      } else if (e.ctrlKey && e.shiftKey && (e.key === 'F' || e.code === 'KeyF')) {
        e.preventDefault()
        this.handleFormat()
      }
    },
    handleFormat() {
      this.code = format(this.code, {
        language: 'postgresql',
        tabWidth: 2,
        keywordCase: 'upper',
        linesBetweenQueries: 1,
      })
    },
    modalClose() {
      this.showResetModal = false
      this.showSchemaModal = false
      this.archive = true
      this.archiveDescription = ''
      this.swapSchema = this.currentSchema
      this.schemaPwd = ''
      this.schemaStatus = 'success'
      this.schemaPwdStatus = 'success'
    },
    handleResetOK() {
      var params = {
        archiveCurrentSession: this.archive,
        archiveDescription: this.archiveDescription,
      }
      this.showResetModal = false
      this.$store.commit(RUNNING, true)
      resetSession(params)
        .then(() => {
          this.handleClear()
          this.$emit('clear')
          this.getCurrentSchema()
          this.$message.success('重置环境成功！')
        })
        .catch((res) => {
          this.$message.error('重置环境失败：' + res.response.data.msg)
        })
        .finally(() => {
          this.$store.commit(RUNNING, false)
        })
    },
    // 运行SQL
    handleRun() {
      if (!this.code.trim()) {
        this.$message.error('请输入 SQL 再运行')
        return
      }
      // 运行选中的值，目前不支持多选
      if (this.$refs.cm && this.codemirror && this.codemirror.doc) {
        const selections = this.codemirror.doc.getSelections()
        if (selections.length > 0 && selections[0].length > 0) {
          this.$emit('runSql', selections[0])
          return
        }
      }
      this.$emit('runSql', this.code)
    },
    handleClear() {
      this.code = ''
    },
    handleClearEnv() {
      this.showResetModal = true
    },
    update() {
      this.$nextTick(() => {
        this.codemirror.scrollIntoView()
      })
    },
    handleSwapSchema() {
      this.showSchemaModal = true
    },
    handleSwapOK() {
      if (!this.schemaPwd.trim()) {
        this.schemaPwdStatus = 'error'
      }

      if (this.schemaStatus == 'error' || this.schemaPwdStatus == 'error') {
        return
      }

      this.$store.commit(RUNNING, true)
      swapSchema({ name: this.swapSchema, password: aesEncrypt(this.schemaPwd) })
        .then(() => {
          this.$message.success('设置用户成功！')
          this.modalClose()
          this.getCurrentSchema()
        })
        .catch((res) => {
          this.$message.error(res.response.data.msg)
        })
        .finally(() => {
          this.$store.commit(RUNNING, false)
        })
    },
    handleTabChange(sql) {
      let cursor = this.codemirror.getSearchCursor(sql)
      if (cursor.findNext()) {
        var from = cursor.from()
        this.codemirror.setCursor(from)
        this.codemirror.scrollIntoView({ line: from.line, ch: 0 }, 10)
      } 
    },
  },
}
</script>

<style lang="less" scoped>
/deep/.CodeMirror-scroll {
  padding: 0;
  margin: 0;
  overflow: auto !important;
}
/deep/.CodeMirror {
  height: 100%;
}
</style>
